import { useNavigate } from "react-router-dom";
import { Settings, LogOut, Bell, ShieldCheck, User as UserIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { isAdminUser } from "@/shared/rbac";

export function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;
  const initials = (user.name || user.email || "?")
    .split(" ")
    .map((s: string) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="user-menu-trigger"
          className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-accent transition-colors"
        >
          <Avatar className="h-8 w-8">
            {user.picture && <AvatarImage src={user.picture} alt={user.name} />}
            <AvatarFallback className="bg-forest-700 text-white text-xs font-semibold">{initials}</AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64" data-testid="user-menu">
        <DropdownMenuLabel className="flex items-center gap-3 py-3">
          <Avatar className="h-9 w-9">
            {user.picture && <AvatarImage src={user.picture} alt={user.name} />}
            <AvatarFallback className="bg-forest-700 text-white text-xs font-semibold">{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">{user.name}</div>
            <div className="text-xs text-muted-foreground truncate font-normal">{user.email}</div>
          </div>
        </DropdownMenuLabel>
        <div className="px-2 pb-2 flex items-center gap-1.5">
          <span className="inline-flex items-center gap-1 rounded-full bg-forest-100 dark:bg-forest-700 text-forest-800 dark:text-forest-100 px-2 py-0.5 text-[11px] font-medium">
            <ShieldCheck className="h-3 w-3" /> {user.role_label || user.role}
          </span>
          {user.is_beta_tester && (
            <span className="rounded-full bg-ai/15 text-ai px-2 py-0.5 text-[11px] font-semibold">Beta</span>
          )}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="user-menu-profile" onClick={() => navigate("/settings")} className="gap-2">
          <UserIcon className="h-4 w-4" /> Profile & Settings
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="user-menu-notifications" onClick={() => navigate("/notifications")} className="gap-2">
          <Bell className="h-4 w-4" /> Notifications
        </DropdownMenuItem>
        {isAdminUser(user) && (
          <DropdownMenuItem data-testid="user-menu-settings" onClick={() => navigate("/settings")} className="gap-2">
            <Settings className="h-4 w-4" /> Organization
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="user-menu-logout" onClick={logout} className="gap-2 text-destructive focus:text-destructive">
          <LogOut className="h-4 w-4" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default UserMenu;
